import { useEffect, useRef, useState } from "react";
import {
    useRive,
    Layout,
    Fit,
    Alignment,
} from "@rive-app/react-canvas";

import "./styles/MentiRiveTest.css";

type AudioContextWithWebkit = typeof AudioContext & {
    new (): AudioContext;
};

export default function MentiRiveTest() {
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef =
        useRef<MediaElementAudioSourceNode | null>(null);

    const animationFrameRef =
        useRef<number | null>(null);

    const [playing, setPlaying] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);

    /*
     * ==========================================
     * RIVE
     * ==========================================
     */

    const { RiveComponent, rive } = useRive({
        src: "/menti/the-robo.riv",

        autoplay: true,

        layout: new Layout({
            fit: Fit.Contain,
            alignment: Alignment.Center,
        }),
    });


    /*
     * ==========================================
     * RIVE DEBUG
     * ==========================================
     */

useEffect(() => {
    if (!rive) {
        return;
    }

    console.log("================================");
    console.log("Menti Rive Loaded");
    console.log("================================");

    console.log(
        "Animations:",
        rive.animationNames
    );

    console.log(
        "State Machines:",
        rive.stateMachineNames
    );

}, [rive]);


    /*
     * ==========================================
     * AUDIO SETUP
     * ==========================================
     */

    const setupAudio = async () => {
        const audio = audioRef.current;

        if (!audio) {
            return;
        }


        /*
         * Browser AudioContext
         */

        const AudioContextClass =
            window.AudioContext ||
            (
                window as typeof window & {
                    webkitAudioContext?:
                        AudioContextWithWebkit;
                }
            ).webkitAudioContext;


        if (!AudioContextClass) {
            console.error(
                "Web Audio API is not supported."
            );

            return;
        }


        /*
         * Create AudioContext once
         */

        if (!audioContextRef.current) {
            audioContextRef.current =
                new AudioContextClass();


            analyserRef.current =
                audioContextRef.current
                    .createAnalyser();


            analyserRef.current.fftSize = 512;

            analyserRef.current
                .smoothingTimeConstant = 0.65;


            sourceRef.current =
                audioContextRef.current
                    .createMediaElementSource(
                        audio
                    );


            sourceRef.current.connect(
                analyserRef.current
            );


            analyserRef.current.connect(
                audioContextRef.current
                    .destination
            );
        }


        /*
         * Resume AudioContext
         */

        if (
            audioContextRef.current.state ===
            "suspended"
        ) {
            await audioContextRef.current.resume();
        }


        startAudioAnalysis();
    };


    /*
     * ==========================================
     * AUDIO ANALYSIS
     * ==========================================
     */

    const startAudioAnalysis = () => {
        const analyser = analyserRef.current;

        if (!analyser) {
            return;
        }


        if (animationFrameRef.current) {
            cancelAnimationFrame(
                animationFrameRef.current
            );
        }


        const data =
            new Uint8Array(
                analyser.frequencyBinCount
            );


        const analyse = () => {

            analyser.getByteFrequencyData(data);


            let total = 0;

            for (
                let i = 0;
                i < data.length;
                i++
            ) {
                total += data[i];
            }


            const average =
                total / data.length;


            /*
             * Normalize audio volume
             */

            const level =
                Math.min(
                    average / 45,
                    1
                );


            setAudioLevel(level);


            animationFrameRef.current =
                requestAnimationFrame(
                    analyse
                );
        };


        analyse();
    };


    /*
     * ==========================================
     * PLAY
     * ==========================================
     */

    const handlePlay = async () => {
        const audio = audioRef.current;

        if (!audio) {
            return;
        }


        try {
            await setupAudio();

            await audio.play();

            setPlaying(true);

        } catch (error) {
            console.error(
                "Audio play error:",
                error
            );
        }
    };


    /*
     * ==========================================
     * PAUSE
     * ==========================================
     */

    const handlePause = () => {
        const audio = audioRef.current;

        if (!audio) {
            return;
        }

        audio.pause();

        setPlaying(false);
    };


    /*
     * ==========================================
     * AUDIO END
     * ==========================================
     */

    const handleEnded = () => {

        setPlaying(false);

        setAudioLevel(0);

        if (animationFrameRef.current) {
            cancelAnimationFrame(
                animationFrameRef.current
            );

            animationFrameRef.current = null;
        }
    };


    /*
     * ==========================================
     * CLEANUP
     * ==========================================
     */

    useEffect(() => {

        return () => {

            if (
                animationFrameRef.current
            ) {
                cancelAnimationFrame(
                    animationFrameRef.current
                );
            }


            if (
                audioContextRef.current
            ) {
                audioContextRef.current
                    .close()
                    .catch(() => {});
            }
        };

    }, []);


    /*
     * ==========================================
     * UI
     * ==========================================
     */

    return (
        <div className="menti-rive-test">

            <div className="menti-rive-card">

                <h2>
                    Menti Rive Test
                </h2>


                <div className="menti-rive-avatar">

                    {RiveComponent && (
                        <RiveComponent />
                    )}

                </div>


                <audio
                    ref={audioRef}
                    src="/menti/menti.wav"
                    preload="auto"
                    onEnded={handleEnded}
                />


                <div className="menti-controls">

                    <button
                        type="button"
                        onClick={handlePlay}
                        disabled={playing}
                    >
                        ▶ Play
                    </button>


                    <button
                        type="button"
                        onClick={handlePause}
                        disabled={!playing}
                    >
                        ⏸ Pause
                    </button>

                </div>


                <div className="menti-debug">

                    <div>
                        Audio Level
                    </div>


                    <div className="meter">

                        <div
                            className="meter-fill"
                            style={{
                                width:
                                    `${audioLevel * 100}%`,
                            }}
                        />

                    </div>


                    <div className="audio-value">
                        {audioLevel.toFixed(2)}
                    </div>

                </div>

            </div>

        </div>
    );
}